import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ActivityStatusTabs, { ACTIVITY_STATUS_TABS } from '../../components/ActivityStatusTabs';
import { HomeNavButton } from '../../components/NavButtons';
import { getStudentExams } from '../../services/examService';
import {
  canOpenSubmittedExam,
  canShowExamResults,
  getExamSubmissionScore,
} from '../../utils/examSettings';
import { focusTheme, getListCardProps } from '../../utils/focusTheme';

const statusConfig = {
  submitted: {
    ...focusTheme.status.submitted,
    label: 'آزمون داده شده',
  },
  active: {
    ...focusTheme.status.active,
    label: 'شروع آزمون',
  },
  upcoming: {
    ...focusTheme.status.upcoming,
    label: 'به‌زودی',
  },
  finished: {
    ...focusTheme.status.finished,
    label: 'پایان یافته',
  },
};

function ArrowCircleIcon({ className = 'text-teal-500' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M8.63612 5.41078L6.69336 7.21477C6.38802 7.4983 6.38802 7.98157 6.69336 8.2651L8.63612 10.0691M7.70447 0.537475C3.74645 0.537475 0.537843 3.74608 0.537843 7.7041C0.537843 11.6621 3.74645 14.8707 7.70447 14.8707C11.6625 14.8707 14.8711 11.6621 14.8711 7.7041C14.8711 3.74608 11.6625 0.537475 7.70447 0.537475Z" stroke="currentColor" strokeWidth="1.07499" strokeLinecap="round" />
    </svg>
  );
}

function formatExamDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}


function hasSubmittedExam(exam) {
  return Boolean(
    exam.submission?.submittedAt ||
      exam.submission?.finishedAt ||
      exam.submission?.status === 'submitted' ||
      exam.submission?.status === 'finished'
  );
}

function getExamViewStatus(exam) {
  if (hasSubmittedExam(exam)) {
    return 'submitted';
  }

  const now = Date.now();
  const startAt = exam.startAt ? new Date(exam.startAt).getTime() : null;
  const endAt = exam.endAt ? new Date(exam.endAt).getTime() : null;

  if (exam.status === 'finished' || (endAt && endAt < now)) {
    return 'finished';
  }

  if (startAt && startAt > now) {
    return 'upcoming';
  }

  return 'active';
}

function ExamsPage() {
  const [exams, setExams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState(ACTIVITY_STATUS_TABS.remaining);

  const remainingExams = useMemo(
    () => exams.filter((exam) => getExamViewStatus(exam) !== 'submitted'),
    [exams]
  );
  const completedExams = useMemo(
    () => exams.filter((exam) => getExamViewStatus(exam) === 'submitted'),
    [exams]
  );
  const visibleExams = activeTab === ACTIVITY_STATUS_TABS.completed ? completedExams : remainingExams;
  const remainingCount = remainingExams.length;
  const completedCount = completedExams.length;
  const nextExam = remainingExams.find((exam) => ['active', 'upcoming'].includes(getExamViewStatus(exam)));

  useEffect(() => {
    let isMounted = true;

    async function loadExams() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const examItems = await getStudentExams();

        if (isMounted) {
          setExams(examItems);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت آزمون‌ها وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadExams();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className='flex items-center gap-2'>
          <p
            style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
            className='px-6 py-1 rounded-[10px] text-[15px] text-white'
          >
            آزمون های من
          </p>

          <img className='h-[50px]' src='/images/exam.webp' alt='' />
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className='text-[14px] font-semibold text-slate-900'>
          لیست آزمون‌ها
        </p>

        <p className='mt-1 text-[13px] font-medium leading-6 text-slate-800'>
          آزمونت رو انتخاب کن، زمان رو ببین و با تمرکز شروع کن.
        </p>
      </div>

      <ActivityStatusTabs
        remainingCount={remainingCount}
        completedCount={completedCount}
        remainingLabel="انجام نشده"
        completedLabel="انجام شده"
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      <div
        className='flex flex-col gap-2 rounded-[8px] p-2'
        style={{ background: focusTheme.sectionBg }}
      >
        {isLoading ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center text-[12px] text-slate-500">
            در حال دریافت آزمون‌ها...
          </div>
        ) : errorMessage ? (
          <div className="rounded-[8px] bg-white px-4 py-5 text-center">
            <p className={`text-[12px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`mt-3 rounded-[6px] px-5 py-1 text-[12px] font-bold ${focusTheme.button.retry}`}
            >
              تلاش دوباره
            </button>
          </div>
        ) : exams.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">فعلا آزمونی نداری</p>
            <p className="mt-2 text-[11px] text-slate-500">وقتی آزمون جدیدی منتشر شود، همینجا نمایش داده می‌شود.</p>
          </div>
        ) : visibleExams.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">
              {activeTab === ACTIVITY_STATUS_TABS.completed
                ? 'هنوز آزمونی ندادی'
                : 'الان آزمون انجام‌نشده‌ای نداری'}
            </p>
            <p className="mt-2 text-[11px] text-slate-500">
              {activeTab === ACTIVITY_STATUS_TABS.completed
                ? 'بعد از دادن آزمون، اینجا نمایش داده می‌شود.'
                : 'همه آزمون‌ها انجام شده‌اند یا هنوز مورد جدیدی منتشر نشده است.'}
            </p>
          </div>
        ) : (
          visibleExams.map((exam, index) => {
            const viewStatus = getExamViewStatus(exam);
            const status = statusConfig[viewStatus] || statusConfig.active;
            const isFinished = viewStatus === 'finished';
            const isSubmitted = viewStatus === 'submitted';
            const canOpen =
              !isFinished && (!isSubmitted || canOpenSubmittedExam(exam));
            const resultScore = isSubmitted && canShowExamResults(exam) ? getExamSubmissionScore(exam) : null;
            const cardProps = getListCardProps(viewStatus, { disabled: isFinished });
            const cardContent = (
              <>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {isSubmitted ? (
                      <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[5px] text-[12px] ${focusTheme.submitted.badge}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                      </span>
                    ) : (
                      <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[5px] text-[12px] font-bold ${status.badgeClass}`}>
                        {index + 1}
                      </span>
                    )}

                    <p className="min-w-0 truncate text-[14px] font-semibold text-slate-900">{exam.title || 'بدون عنوان'}</p>
                  </div>

                  {isFinished ? (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${status.badgeClass}`}>
                      غیرفعال
                    </span>
                  ) : canOpen ? (
                    <ArrowCircleIcon className={status.iconClass} />
                  ) : (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${status.badgeClass}`}>
                      بدون مرور
                    </span>
                  )}
                </div>

                {!isSubmitted && exam.description?.trim() && (
                  <p className="line-clamp-2 text-[13px] leading-6 text-slate-700">
                    {exam.description}
                  </p>
                )}

                <div className={`grid ${isSubmitted ? (resultScore != null ? 'grid-cols-3' : 'grid-cols-2') : 'grid-cols-3'} gap-2 text-[11px]`}>
                  <div className={focusTheme.meta.chip}>
                    شروع: {formatExamDate(exam.startAt)}
                  </div>

                  {!isSubmitted && (
                    <div className={`${focusTheme.meta.chip} text-center`}>
                      {exam.durationMinutes || 0} دقیقه
                    </div>
                  )}

                  {resultScore != null && (
                    <div className={`${focusTheme.meta.chip} text-center`}>
                      نمره: {resultScore}
                      {exam.maxScore != null ? ` / ${exam.maxScore}` : ''}
                    </div>
                  )}

                  <div className={`${focusTheme.meta.status} ${status.labelClass}`}>
                    {status.label}
                  </div>
                </div>
              </>
            );

            if (!canOpen) {
              return (
                <div
                  key={exam.id}
                  className={cardProps.className}
                  style={cardProps.style}
                  aria-disabled="true"
                >
                  {cardContent}
                </div>
              );
            }

            return (
              <Link
                key={exam.id}
                to={`/exams/${exam.id}`}
                className={cardProps.className}
                style={cardProps.style}
              >
                {cardContent}
              </Link>
            );
          })
        )}
      </div>

      {activeTab === ACTIVITY_STATUS_TABS.remaining && (
        <div className='w-full mx-auto flex flex-col gap-2 items-center justify-center'>
          <p className='text-[13px] font-medium text-slate-800'>
            قبل از شروع آزمون، یک نفس عمیق بکش و آماده شو :)
          </p>

          {nextExam && (
            <Link
              to={`/exams/${nextExam.id}`}
              className={`w-11/12 mx-auto text-[14px] font-bold py-2.5 text-center flex items-center justify-center rounded-[8px] ${focusTheme.button.primary}`}
            >
              مشاهده آزمون بعدی
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default ExamsPage;
